/**
 * Registry مرکزی دسته‌های عملیاتی زنجیره تأمین.
 * اضافه‌کردن دستهٔ جدید = یک آبجکت اینجا (+ قالب در movementTemplates در صورت نیاز).
 */
import {
  ShoppingOutlined, ScissorOutlined, BgColorsOutlined,
  InboxOutlined, PrinterOutlined, TagsOutlined, AppstoreOutlined, ToolOutlined,
} from "@ant-design/icons";
import React from "react";

/** دسته‌های فعال و آینده */
export const SUPPLY_CATEGORIES = [
  {
    key: "yarn_purchase",
    label: "خرید نخ",
    icon: "ShoppingOutlined",
    order: 10,
    enabled: true,
    perm: "supply_chain.create",
    blurb: "خرید نخ از هر تأمین‌کننده (پیش‌فرض: درین‌ریس) و ارسال به بافندگی",
    partyTypes: ["supplier", "knitting_workshop"],
    operations: [
      { event_type: "yarn_purchased", label: "خرید نخ", defaultParties: { source: "supplier" } },
    ],
  },
  {
    key: "knitting",
    label: "بافندگی",
    icon: "ScissorOutlined",
    order: 20,
    enabled: true,
    perm: "supply_chain.create",
    blurb: "خروج پارچهٔ خام از بافندگی (نوربخش، خوشباف یا هر بافندهٔ دیگر) به‌سوی رنگرزی",
    partyTypes: ["knitting_workshop"],
    operations: [
      { event_type: "raw_fabric_sent_to_dyeing", label: "خروجی بافندگی", defaultParties: { dest: "dyeing_provider" } },
    ],
  },
  {
    key: "dyeing",
    label: "رنگرزی",
    icon: "BgColorsOutlined",
    order: 30,
    enabled: true,
    perm: "supply_chain.create",
    blurb: "دریافت پارچهٔ رنگ‌شده از رنگرزی (پیش‌فرض پوشان) و ورود به انبار",
    partyTypes: ["dyeing_provider", "internal_warehouse"],
    operations: [
      { event_type: "dyed_fabric_received_in_gharchak_warehouse", label: "خروجی رنگرزی / ورود انبار", defaultParties: { source: "dyeing_provider", dest: "internal_warehouse" } },
    ],
  },
  // آینده — disabled تا فعال شوند
  { key: "carton", label: "کارتن", icon: "InboxOutlined", order: 40, enabled: false, perm: "supply_chain.create", blurb: "", partyTypes: [], operations: [] },
  { key: "box", label: "جعبه‌سازی", icon: "AppstoreOutlined", order: 50, enabled: false, perm: "supply_chain.create", blurb: "", partyTypes: [], operations: [] },
  { key: "print", label: "چاپ", icon: "PrinterOutlined", order: 60, enabled: false, perm: "supply_chain.create", blurb: "", partyTypes: [], operations: [] },
  { key: "packaging", label: "بسته‌بندی", icon: "InboxOutlined", order: 70, enabled: false, perm: "supply_chain.create", blurb: "", partyTypes: [], operations: [] },
  { key: "label", label: "لیبل", icon: "TagsOutlined", order: 80, enabled: false, perm: "supply_chain.create", blurb: "", partyTypes: [], operations: [] },
  { key: "other", label: "سایر خدمات", icon: "ToolOutlined", order: 90, enabled: false, perm: "supply_chain.create", blurb: "", partyTypes: [], operations: [] },
];

const ICON_MAP = {
  ShoppingOutlined, ScissorOutlined, BgColorsOutlined,
  InboxOutlined, PrinterOutlined, TagsOutlined, AppstoreOutlined, ToolOutlined,
};

export function categoryIcon(keyOrName, props = {}) {
  const cat = typeof keyOrName === "string" && !ICON_MAP[keyOrName]
    ? SUPPLY_CATEGORIES.find((c) => c.key === keyOrName)
    : null;
  const name = cat ? cat.icon : keyOrName;
  const Comp = ICON_MAP[name] || AppstoreOutlined;
  return React.createElement(Comp, props);
}

export function enabledCategories() {
  return SUPPLY_CATEGORIES.filter((c) => c.enabled).sort((a, b) => a.order - b.order);
}

export function getCategory(key) {
  return SUPPLY_CATEGORIES.find((c) => c.key === key) || null;
}

export function operationsFor(categoryKey) {
  const c = getCategory(categoryKey);
  return c ? (c.operations || []) : [];
}

/** نگاشت event_typeهای قدیمی به دستهٔ Canonical (برای فیلتر/گزارش؛ داده حذف نمی‌شود) */
export const LEGACY_EVENT_CATEGORY = {
  yarn_sent_to_knitter: "knitting",
  raw_fabric_produced: "knitting",
  dyed_fabric_received_from_dyeing: "dyeing",
};

/** انواع منسوخ که نباید در منوی ثبت / فیلتر نوع عملیات دیده شوند */
export const OBSOLETE_EVENT_TYPES = new Set([
  "yarn_sent_to_knitter",
  "raw_fabric_produced",
  "dyed_fabric_received_from_dyeing",
]);

/** event_type → دسته (اولین تطبیق در دسته‌های فعال، سپس legacy map) */
export function categoryOfEventType(eventType) {
  for (const c of SUPPLY_CATEGORIES) {
    if ((c.operations || []).some((o) => o.event_type === eventType)) return c;
  }
  const legacyKey = LEGACY_EVENT_CATEGORY[eventType];
  if (legacyKey) return getCategory(legacyKey);
  return null;
}

export function categoryLabelOfEventType(eventType) {
  return categoryOfEventType(eventType)?.label || "—";
}

/** همهٔ event_typeهای متعلق به یک دسته (فعال + legacy) برای فیلتر */
export function eventTypesForCategory(categoryKey) {
  const c = getCategory(categoryKey);
  const types = new Set((c?.operations || []).map((o) => o.event_type));
  Object.entries(LEGACY_EVENT_CATEGORY).forEach(([et, cat]) => {
    if (cat === categoryKey) types.add(et);
  });
  return [...types];
}

/** فیلتر طرف‌حساب بر اساس دسته / عملیات */
export function partyFilterFor(categoryKey, role = "any") {
  const c = getCategory(categoryKey);
  if (!c) return null;
  if (role === "source" && categoryKey === "yarn_purchase") return ["supplier"];
  if (role === "dest" && categoryKey === "yarn_purchase") return ["knitting_workshop"];
  if (role === "source" && categoryKey === "knitting") return ["knitting_workshop"];
  if (role === "dest" && categoryKey === "knitting") return ["dyeing_provider"];
  if (role === "source" && categoryKey === "dyeing") return ["dyeing_provider"];
  if (role === "dest" && categoryKey === "dyeing") return ["internal_warehouse"];
  return c.partyTypes || null;
}

/** گزینه‌های رنگ متداول برای Select (قابل گسترش) */
export const COMMON_COLORS = [
  { value: "سفید", label: "سفید" },
  { value: "مشکی", label: "مشکی" },
  { value: "خاکستری", label: "خاکستری" },
  { value: "آبی", label: "آبی" },
  { value: "قرمز", label: "قرمز" },
  { value: "سبز", label: "سبز" },
  { value: "کرم", label: "کرم" },
  { value: "سایر", label: "سایر" },
];

/** لینک منوی ثبت برای هر دسته */
export function newInvoicePath(categoryKey, eventType) {
  const q = new URLSearchParams();
  if (categoryKey) q.set("category", categoryKey);
  if (eventType) q.set("type", eventType);
  const s = q.toString();
  return s ? `/supply-chain/new?${s}` : "/supply-chain/new";
}
